import {
  ATTACHMENT_ALLOWED_EXTENSIONS,
  ATTACHMENT_MAX_FILE_SIZE,
} from "../constants/containerOrderConstants";
import type { ContainerLineAttachment } from "../types/containerOrders";

/** Lower-cased extension of a filename, or "" when it has none. */
export const extensionOf = (name: string) =>
  name.split(".").pop()?.toLowerCase() ?? "";

/** Why a picked file was not accepted. Each maps to one `attachments.*` string. */
export type AttachmentRejection =
  | { reason: "type"; name: string }
  | { reason: "size"; name: string }
  | { reason: "count"; name: string };

export interface ScreenedAttachments {
  kept: File[];
  rejected: AttachmentRejection[];
}

/**
 * Drop the files the API would refuse, and say which and why.
 *
 * Without this the whole save round-trips and comes back as one rejection
 * naming neither the file nor the rule, after the user has already filled in the
 * rest of the form. `room` is what is left of this zone's per-save budget.
 *
 * Shared by every drop zone in the container module — the order's, one
 * container's, and the driver's — so all three accept exactly the same files.
 * Three copies of these three rules is three chances for the phone to allow what
 * the office refuses. The reasons come back as data rather than as toasts so
 * each screen can phrase them in its own namespace and, more importantly, so
 * this can be called from inside or outside a state updater without deciding
 * that for the caller.
 *
 * The server remains the authority; this is a courtesy, not a guarantee.
 */
export function screenAttachments(
  selected: File[],
  room: number,
): ScreenedAttachments {
  const kept: File[] = [];
  const rejected: AttachmentRejection[] = [];

  for (const file of selected) {
    if (!ATTACHMENT_ALLOWED_EXTENSIONS.includes(extensionOf(file.name))) {
      rejected.push({ reason: "type", name: file.name });
      continue;
    }
    if (file.size > ATTACHMENT_MAX_FILE_SIZE) {
      rejected.push({ reason: "size", name: file.name });
      continue;
    }
    if (kept.length >= room) {
      rejected.push({ reason: "count", name: file.name });
      continue;
    }
    kept.push(file);
  }

  return { kept, rejected };
}

/** Enough of an axios blob response for `deliverAttachment` to work with. */
interface AttachmentResponse {
  data: Blob | ArrayBuffer;
  headers?: Record<string, unknown>;
}

/** The stored facts about a file needed to hand it to the browser. */
type DeliverableAttachment = { originalName: string; mimeType?: string };

/**
 * Hand a downloaded document to the browser — saved, or opened for preview.
 *
 * The type matters more than it looks. A blob built as `new Blob([data])` has
 * an empty type, and an empty type is not "unknown, work it out" — it makes the
 * browser sniff, and the fallback is `text/plain`. Saving still worked, because
 * the extension on `link.download` tells the OS what the file is; *previewing*
 * showed a PDF's raw bytes as pages of mojibake. So the type is set explicitly:
 * what the server said it was sending, else what was recorded at upload, else
 * a generic binary — which at worst prompts a save rather than rendering the
 * bytes as text.
 *
 * (`responseType: "blob"` already hands back a typed Blob; re-wrapping it is
 * what dropped the type. This keeps the re-wrap, because the same call is used
 * for arraybuffer responses, and states the type either way.)
 *
 * Shared by every attachment list in the container module — the order's, one
 * container's, and the driver's — because all four had the same bug.
 */
export function deliverAttachment(
  res: AttachmentResponse,
  attachment: DeliverableAttachment,
  previewOnly = false,
): void {
  const headerType = res.headers?.["content-type"];
  const type =
    (typeof headerType === "string" && headerType.split(";")[0].trim()) ||
    attachment.mimeType ||
    "application/octet-stream";

  const url = window.URL.createObjectURL(new Blob([res.data], { type }));

  if (previewOnly) {
    window.open(url, "_blank");
    // Give the new tab time to claim the blob before revoking it.
    setTimeout(() => window.URL.revokeObjectURL(url), 10000);
    return;
  }

  const link = document.createElement("a");
  link.href = url;
  link.download = attachment.originalName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

/** Who is looking at a container's documents. */
export interface AttachmentViewer {
  /** The signed-in user's id. */
  userId?: string;
  /** True on the driver's own execution screen. */
  isDriver: boolean;
}

/**
 * Whether this viewer may remove this document.
 *
 * The mirror of `canRemoveLineAttachment` in the container order controller,
 * which is the rule that actually holds: the office owns what the office filed
 * and any admin or user may remove it, while a driver's upload is that driver's
 * alone — not the office's, and not the next driver's after a reassignment.
 *
 * Kept client-side only so the bin icon appears exactly where pressing it would
 * work. A row that shows no bin is not a row that is protected; the API is.
 */
export function canRemoveAttachment(
  attachment: Pick<ContainerLineAttachment, "uploadedBy" | "uploadedByRole">,
  viewer: AttachmentViewer,
): boolean {
  if (attachment.uploadedByRole === "driver") {
    return (
      viewer.isDriver &&
      !!viewer.userId &&
      attachment.uploadedBy?._id === viewer.userId
    );
  }

  // Office uploads — and rows from before the field existed — are the office's.
  return !viewer.isDriver;
}

/** The uploader's name, for the line under a document. */
export const uploaderName = (
  attachment: Pick<ContainerLineAttachment, "uploadedBy">,
): string =>
  `${attachment.uploadedBy?.firstName ?? ""} ${
    attachment.uploadedBy?.lastName ?? ""
  }`.trim();
